import type { NodeViewProps } from '@tiptap/react';
import { useCallback, useEffect, useState } from 'react';

type Props = Pick<NodeViewProps, 'node' | 'editor' | 'getPos'>;

const FIRST_CHILD_NODE_POSITION = 1;
const MAX_ALT_TEXT_LENGTH = 85;

// This is a TEMPORARY SOLUTION. This is also to ensure that we return valid HTML for images.
// At the moment images have no alt attribute or the alt attribute is empty.
// This hook is used to set the alt attribute of an image based on the caption text if it's not longer than 85 characters.
// If there is no caption text or the caption text is too long, we set the outer container to aria-hidden=true by isHiddenImageContent.
// Currently we are discussing a solution which includes an additional alt text field in the editor or the imaage cropper modal.
export const useImageAlt = ({ node, editor, getPos }: Props): boolean => {
  const [altText, setAltText] = useState<string>('');
  const [isHiddenImageContent, setIsHiddenImageContent] = useState(false);

  const getAltTextFromCaption = useCallback(
    (text: string | undefined): string | null => {
      if (text && text.length > 0 && text.length <= MAX_ALT_TEXT_LENGTH) {
        return text;
      }
      return null;
    },
    []
  );

  useEffect(() => {
    node.content.forEach((child) => {
      if (child.type.name === 'imageCaption') {
        const text = getAltTextFromCaption(child.firstChild?.text);

        if (text) {
          setAltText(text);
        }
      }
    });
  }, [node, getAltTextFromCaption]);

  useEffect(() => {
    if (!editor.isEditable) {
      const currentAlt = node.content?.firstChild?.attrs?.alt;
      if (currentAlt === altText) return;

      editor.commands.command(({ tr }) => {
        const position = getPos() + FIRST_CHILD_NODE_POSITION;

        tr.setNodeMarkup(position, undefined, {
          ...node.content.firstChild?.attrs,
          alt: altText,
        });

        return true;
      });
    }

    setIsHiddenImageContent(!altText);
  }, [editor, getPos, node, altText]);

  return isHiddenImageContent;
};
